import React, { useState } from "react";
import {
  Box, Paper, CardContent, TextField, Grid, Button, Stepper, Step, StepLabel,
  LinearProgress, Typography, RadioGroup, FormControlLabel, Radio, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { motion, AnimatePresence } from "framer-motion";

const stepData = [
  { label: "Контакт" },
  { label: "Замърсявания" },
  { label: "Почистване" },
  { label: "Потвърждение" }
];

const pollutantOptions = {
  dust: "Прах",
  bees: "Замърсяване от пчели",
  birds: "Замърсяване от птици",
  limescale: "Котлен камък",
  lichens: "Лишеи / мъх",
  rust: "Ръжда/метални оксиди",
  cement: "Циментов прах",
  paint: "Замърсяване от боя"
};

export default function FancyWizard({ submitUrl = "/api/submit.php", recaptchaKey = "6LdiUyMsAAAAAB-jAieKKClmVR_z-WndzJVWImT7" }) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", contactEmail: "", contactPhone: "",
    pollutants: { dust: false, bees: false, birds: false, limescale: false, lichens: false, rust: false, cement: false, paint: false, other: false, otherText: "" },
    region: "", cleanedBefore: "", cleaningMethod: "", cleaningDescription: "",
    parkPower: "", panelSpecs: "", panelCountOrArea: "", source: ""
  });
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const togglePollutant = (key) => setForm(p => ({
    ...p,
    pollutants: { ...p.pollutants, [key]: !p.pollutants[key] }
  }));

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    const invalid = files.filter(f => !f.type.startsWith("image/"));
    if (invalid.length > 0) { setImageError("Моля изберете само изображения (jpg/png)"); return; }
    if (files.length > 3) { setImageError("Можете да прикачите максимум 3 изображения"); return; }
    setImageError(""); setImages(files);
  };

  const validateStep = () => {
    const err = {};
    if (step === 0) {
      if (!form.firstName.trim()) err.firstName = "Изисква се име";
      if (!form.lastName.trim()) err.lastName = "Изисква се фамилия";
      if (!form.contactEmail.includes('@')) err.contactEmail = "Невалиден email";
      if (form.contactPhone.length < 6) err.contactPhone = "Невалиден телефон";
      if (!form.source) err.source = "Моля изберете откъде научихте за нас";
    }
    if (step === 1) {
      const hasOne = Object.keys(form.pollutants).some(k => k !== 'otherText' && form.pollutants[k]);
      if (!hasOne) err.pollutants = "Моля изберете поне едно замърсяване";
      if (!form.region.trim()) err.region = "Моля въведете район";
    }
    if (step === 2 && form.cleanedBefore === "yes" && !form.cleaningMethod.trim()) {
      err.cleaningMethod = "Моля посочете метод на почистване";
    }
    if (step === 3 && !recaptchaToken) {
      err.recaptcha = "Моля потвърдете, че не сте робот";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const nextStep = () => { if (validateStep()) setStep(s => Math.min(s + 1, stepData.length - 1)); };
  const prevStep = () => setStep(s => Math.max(s - 0, 0));

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    const formData = new FormData();
    Object.keys(form).forEach(key => formData.append(key, typeof form[key] === 'object' ? JSON.stringify(form[key]) : form[key]));
    images.forEach(file => { if (file) formData.append("images[]", file); });
    formData.append("recaptchaToken", recaptchaToken || "");

    try {
      const resp = await fetch(submitUrl, { method: "POST", body: formData });
      const text = await resp.text();
      if (!resp.ok) throw new Error(text || "Грешка при изпращане");
      setStatusMessage({ type: "success", text: "Анкетата беше изпратена успешно!" });
      setSubmitted(true);
    } catch (err) {
      setStatusMessage({ type: "error", text: err.message });
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm({
      firstName: "", lastName: "", contactEmail: "", contactPhone: "",
      pollutants: { dust: false, bees: false, birds: false, limescale: false, lichens: false, rust: false, cement: false, paint: false, other: false, otherText: "" },
      region: "", cleanedBefore: "", cleaningMethod: "", cleaningDescription: "",
      parkPower: "", panelSpecs: "", panelCountOrArea: "", source: ""
    });
    setImages([]);
    setRecaptchaToken(null);
    setStep(0);
    setErrors({});
    setStatusMessage(null);
  };

  const cardStyle = { borderRadius: 4, boxShadow: "0 4px 10px rgba(47,79,127,0.18)", padding: "2rem", backgroundColor: "#f9f9f9" };
  const inputSpacing = { marginBottom: "1rem" };
  const variants = { enter: { opacity: 0, x: 100 }, center: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -100 } };
  const emojiIcons = ["👤", "🌿", "🧽", "✅"];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 6, width: "100%" }}>
      <Box sx={{ width: "100%", maxWidth: 900, mx: "auto", px: 2 }}>
        {/* Stepper */}
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {stepData.map((s, i) => {
            const isActive = step === i;
            const isCompleted = step > i;
            const iconColor = isCompleted ? "#42a5f5" : isActive ? "#2f4f7f" : "#b0bec5";
            return (
              <Step key={i} onClick={() => setStep(i)} sx={{ cursor: "pointer" }}>
                <StepLabel
                  icon={<span style={{ fontSize: 28, color: iconColor }}>{emojiIcons[i]}</span>}
                  sx={{
                    "& .MuiStepIcon-root.Mui-active": { color: iconColor },
                    "& .MuiStepIcon-root.Mui-completed": { color: iconColor }
                  }}
                >{s.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <LinearProgress variant="determinate" value={(step / (stepData.length - 1)) * 100} sx={{ my: 3, height: 10, borderRadius: 2 }} />

        {statusMessage && <Typography color={statusMessage.type === "error" ? "error" : "primary"} sx={{ mb: 3, textAlign: "center", fontWeight: "bold" }}>{statusMessage.text}</Typography>}

        <AnimatePresence exitBeforeEnter>
          {submitted ? (
            <motion.div key="success" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
              <Paper elevation={4} sx={cardStyle}>
                <CardContent sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold", color: "#2f4f7f" }}>Анкетата беше изпратена успешно! ✅</Typography>
                  <Typography variant="body1" sx={{ mb: 4 }}>Благодарим ви за попълването. Ще се свържем с вас скоро.</Typography>
                  <Button variant="contained" onClick={resetForm} sx={{ px: 5, py: 1.5, background: 'linear-gradient(90deg,#2f4f7f 0%,#42a5f5 100%)', fontWeight: "bold", "&:hover": { transform: "scale(1.05)" } }}>Попълнете нова анкета</Button>
                </CardContent>
              </Paper>
            </motion.div>
          ) : (
            <motion.div key={step} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
              <Paper elevation={4} sx={cardStyle}>
                <CardContent>

                  {/* Step 0: Contact */}
                  {step === 0 && <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}><TextField label="Име" fullWidth value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} sx={inputSpacing} error={!!errors.firstName} helperText={errors.firstName} /></Grid>
                    <Grid item xs={12} sm={6}><TextField label="Фамилия" fullWidth value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} sx={inputSpacing} error={!!errors.lastName} helperText={errors.lastName} /></Grid>
                    <Grid item xs={12} sm={6}><TextField label="Email" fullWidth value={form.contactEmail} onChange={e => setForm({ ...form, contactEmail: e.target.value })} sx={inputSpacing} error={!!errors.contactEmail} helperText={errors.contactEmail} /></Grid>
                    <Grid item xs={12} sm={6}><TextField label="Телефон" fullWidth value={form.contactPhone} onChange={e => setForm({ ...form, contactPhone: e.target.value })} sx={inputSpacing} error={!!errors.contactPhone} helperText={errors.contactPhone} /></Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth sx={inputSpacing} error={!!errors.source}>
                        <InputLabel>От къде научихте за нас</InputLabel>
                        <Select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                          <MenuItem value="google">Google</MenuItem>
                          <MenuItem value="social">Социални мрежи</MenuItem>
                          <MenuItem value="friend">Препоръка</MenuItem>
                          <MenuItem value="other">Друго</MenuItem>
                        </Select>
                        {errors.source && <Typography color="error">{errors.source}</Typography>}
                      </FormControl>
                    </Grid>
                  </Grid>}

                  {/* Step 1: Pollutants */}
                  {step === 1 && <Box>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>Какви замърсявания са налични?</Typography>
                    {errors.pollutants && <Typography color="error" sx={{ mb: 1, textAlign: "center" }}>{errors.pollutants}</Typography>}
                    <Grid container spacing={2}>
                      {Object.entries(pollutantOptions).map(([key, label]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Box onClick={() => togglePollutant(key)} sx={{ border: '1px solid', borderColor: form.pollutants[key] ? 'primary.main' : 'grey.400', p: 2, cursor: 'pointer', backgroundColor: form.pollutants[key] ? 'primary.light' : 'background.paper', textAlign: 'center', mb: 1, '&:hover': { boxShadow: 3 } }}>{label}</Box>
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={6} md={4}>
                        <Box onClick={() => togglePollutant('other')} sx={{ border: '1px solid', borderColor: form.pollutants.other ? 'primary.main' : 'grey.400', p: 2, cursor: 'pointer', backgroundColor: form.pollutants.other ? 'primary.light' : 'background.paper', textAlign: 'center', mb: 1, '&:hover': { boxShadow: 3 } }}>Други</Box>
                        {form.pollutants.other && <TextField label="Моля посочете" fullWidth value={form.pollutants.otherText} onChange={e => setForm({ ...form, pollutants: { ...form.pollutants, otherText: e.target.value } })} sx={{ mt: 1 }} />}
                      </Grid>
                      <Grid item xs={12}><TextField label="Район" fullWidth value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} sx={{ mt: 2 }} error={!!errors.region} helperText={errors.region} /></Grid>
                    </Grid>
                  </Box>}

                  {/* Step 2: Cleaning */}
                  {step === 2 && <Box>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>Било ли е почиствано досега?</Typography>
                    <RadioGroup row value={form.cleanedBefore} onChange={e => setForm({ ...form, cleanedBefore: e.target.value })} sx={{ mb: 2, justifyContent: "center" }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Да" />
                      <FormControlLabel value="no" control={<Radio />} label="Не" />
                    </RadioGroup>
                    {form.cleanedBefore === "yes" && <TextField label="С какъв метод" fullWidth value={form.cleaningMethod} onChange={e => setForm({ ...form, cleaningMethod: e.target.value })} sx={{ mb: 2 }} error={!!errors.cleaningMethod} helperText={errors.cleaningMethod} />}
                    <TextField label="Мощност на парка" fullWidth value={form.parkPower} onChange={e => setForm({ ...form, parkPower: e.target.value })} sx={{ mb: 2 }} />
                    <TextField label="Характеристики на панела" fullWidth value={form.panelSpecs} onChange={e => setForm({ ...form, panelSpecs: e.target.value })} sx={{ mb: 2 }} />
                    <TextField label="Брой панели / Площ" fullWidth value={form.panelCountOrArea} onChange={e => setForm({ ...form, panelCountOrArea: e.target.value })} sx={{ mb: 2 }} />

                    <Box sx={{ mt: 2, textAlign: "center" }}>
                      <input id="file-upload" type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
                      <label htmlFor="file-upload">
                        <Button variant="contained" component="span" sx={{ background: 'linear-gradient(90deg,#2f4f7f 0%,#42a5f5 100%)', color: '#fff', px: 4, py: 1.5, '&:hover': { background: 'linear-gradient(90deg,#1565c0 0%,#1e88e5 100%)' } }}>Прикачи снимки</Button>
                      </label>
                      {imageError && <Typography color="error" sx={{ mt: 1 }}>{imageError}</Typography>}
                      <Box sx={{ mt: 1 }}>{images.length > 0 ? images.map(f => f.name).join(', ') : <Typography color="text.secondary">Няма избрани изображения</Typography>}</Box>
                    </Box>
                  </Box>}

                  {/* Step 3: ReCAPTCHA */}
                  {step === 3 && (
                    <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", width: "100%" }}>
                      {errors.recaptcha && <Typography color="error" sx={{ mb: 2 }}>{errors.recaptcha}</Typography>}
                      <ReCAPTCHA sitekey={recaptchaKey} onChange={token => setRecaptchaToken(token)} />
                    </Box>
                  )}

                  {/* Navigation Buttons */}
                  <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2, mt: 4 }}>
                    <Button variant="outlined" disabled={step === 0 || submitting} onClick={prevStep} sx={{ px: 3, py: 1.5 }}>Назад</Button>
                    {step < stepData.length - 1 ? (
                      <Button variant="contained" onClick={nextStep} sx={{ px: 4, py: 1.5, background: 'linear-gradient(90deg,#2f4f7f 0%,#42a5f5 100%)', "&:hover": { background: 'linear-gradient(90deg,#1565c0 0%,#1e88e5 100%)', transform: "scale(1.05)" } }}>Напред</Button>
                    ) : (
                      <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ px: 4, py: 1.5, background: 'linear-gradient(90deg,#2f4f7f 0%,#42a5f5 100%)', "&:hover": { background: 'linear-gradient(90deg,#1565c0 0%,#1e88e5 100%)', transform: "scale(1.05)" } }}>
                        {submitting ? "Изпращане..." : "Изпрати"}
                      </Button>
                    )}
                  </Box>

                </CardContent>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
